"use strict";
/**
 * Reliability Data Types for ARP4761A Safety Assessment
 *
 * All failure rate data MUST have auditable provenance for certification.
 * This module defines the types for:
 * - Reliability data sources (MIL-HDBK-217, vendor data, industry databases)
 * - Failure rate records with citations
 * - Data provenance and traceability
 *
 * COMPLIANCE: All failure rates used in safety analyses trace back to
 * approved, versioned source documents via these types.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertFailureRate = convertFailureRate;
exports.formatFailureRate = formatFailureRate;
exports.formatCitation = formatCitation;
exports.generateReliabilityId = generateReliabilityId;
exports.isSourceValidForUse = isSourceValidForUse;
exports.getRecordWarnings = getRecordWarnings;
// ===== Utility Functions =====
/**
 * Convert failure rate between units
 */
function convertFailureRate(value, fromUnit, toUnit) {
    if (fromUnit === toUnit)
        return value;
    // Convert to per_hour first
    let perHour;
    switch (fromUnit) {
        case 'per_hour':
            perHour = value;
            break;
        case 'per_million_hours':
            perHour = value / 1e6;
            break;
        case 'FIT':
            perHour = value / 1e9;
            break;
    }
    // Convert to target unit
    switch (toUnit) {
        case 'per_hour':
            return perHour;
        case 'per_million_hours':
            return perHour * 1e6;
        case 'FIT':
            return perHour * 1e9;
    }
}
/**
 * Format a failure rate for display
 */
function formatFailureRate(value, unit, precision = 3) {
    const formatted = value.toExponential(precision);
    switch (unit) {
        case 'per_hour':
            return `${formatted}/hr`;
        case 'per_million_hours':
            return `${formatted}/10⁶ hr`;
        case 'FIT':
            return `${formatted} FIT`;
    }
}
/**
 * Format a citation for display
 */
function formatCitation(citation) {
    const parts = [citation.sourceDocument];
    if (citation.section)
        parts.push(citation.section);
    if (citation.table)
        parts.push(citation.table);
    if (citation.equation)
        parts.push(citation.equation);
    if (citation.pageNumber)
        parts.push(`p. ${citation.pageNumber}`);
    return parts.join(', ');
}
/**
 * Generate a unique record ID
 */
function generateReliabilityId(prefix) {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 8);
    return `${prefix}-${timestamp}-${random}`;
}
/**
 * Check if a reliability source is valid for use
 */
function isSourceValidForUse(source) {
    return source.approvalStatus === 'approved';
}
/**
 * Get warnings for a failure rate record
 */
function getRecordWarnings(record, source) {
    const warnings = [];
    if (source.approvalStatus !== 'approved') {
        warnings.push(`Source "${source.sourceName}" is not approved (status: ${source.approvalStatus})`);
    }
    if (source.approvalStatus === 'deprecated') {
        warnings.push(`Source "${source.sourceName}" is deprecated and should be replaced`);
    }
    if (!record.citation.verifiedBy) {
        warnings.push('Citation has not been verified by an engineer');
    }
    return warnings;
}
//# sourceMappingURL=reliability.js.map